'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import Button from './components/ui/buttons/button'

export default function NotFound() {
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        router.push('/')
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [router])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-16 sm:px-6 sm:py-24 md:grid md:place-items-center lg:px-8">
      <main className="mx-auto w-full max-w-2xl" role="main" aria-labelledby="error-heading">
        <div className="text-center">
          <div className="relative mx-auto mb-8 h-40 w-40 sm:h-48 sm:w-48" aria-hidden="true">
            <svg
              className="h-full w-full text-primary/20"
              viewBox="0 0 200 200"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label="404 Error Illustration"
            >
              <circle cx="100" cy="100" r="80" stroke="currentColor" strokeWidth="2" />
              <path
                d="M 70 80 Q 70 70 80 70 Q 90 70 90 80"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M 110 80 Q 110 70 120 70 Q 130 70 130 80"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path d="M 70 130 Q 100 110 130 130" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-6xl font-bold text-primary sm:text-7xl" aria-hidden="true">
                404
              </span>
            </div>
          </div>

          <h1 id="error-heading" className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            We can't find that page
          </h1>

          <p className="mx-auto mt-6 max-w-md text-base leading-7 text-gray-600">
            The page you're looking for might have been moved, renamed, or doesn't exist. Let's get you back on track.
          </p>

          <form onSubmit={handleSearch} className="mx-auto mt-8 max-w-md" role="search">
            <label htmlFor="search-404" className="sr-only">
              Search for what you need
            </label>
            <div className="flex gap-2">
              <input
                id="search-404"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for what you need..."
                className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                aria-label="Search query"
              />
              <Button type="submit" variant="primary" size="md" className="normal-case" aria-label="Submit search">
                Search
              </Button>
            </div>
          </form>

          <div
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
            role="navigation"
            aria-label="Error page navigation"
          >
            <Button
              onClick={() => router.back()}
              variant="outline"
              size="lg"
              className="min-w-[140px] normal-case"
              aria-label="Go back to previous page"
            >
              <svg
                className="mr-2 inline h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Go back
            </Button>

            <Link href="/" className="inline-block">
              <Button variant="primary" size="lg" className="min-w-[140px] normal-case" aria-label="Go to homepage">
                <svg
                  className="mr-2 inline h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Go to homepage
              </Button>
            </Link>
          </div>

          <div className="mt-8 text-sm text-gray-500">
            <p className="mb-2">Here are some helpful links instead:</p>
            <nav aria-label="Helpful links">
              <ul className="flex flex-wrap justify-center gap-4 text-primary">
                <li>
                  <Link
                    href="/events"
                    className="rounded px-1 hover:text-primary/80 hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    Events
                  </Link>
                </li>
                <li>
                  <Link
                    href="/marketplace"
                    className="rounded px-1 hover:text-primary/80 hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    Marketplace
                  </Link>
                </li>
                <li>
                  <Link
                    href="/skyward"
                    className="rounded px-1 hover:text-primary/80 hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    Student Fees
                  </Link>
                </li>
                <li>
                  <Link
                    href="/help"
                    className="rounded px-1 hover:text-primary/80 hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    Help Center
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          <div className="mt-12 border-t border-gray-200 pt-8">
            <p className="text-sm text-gray-500">
              Still need help?{' '}
              <a
                href="mailto:support@payk12.com"
                className="rounded px-1 font-medium text-primary hover:text-primary/80 hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20"
                aria-label="Contact support via email"
              >
                Contact our support team
              </a>{' '}
              or call{' '}
              <a
                href="tel:1-800-555-0123"
                className="rounded px-1 font-medium text-primary hover:text-primary/80 hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20"
                aria-label="Contact support via phone"
              >
                1-800-555-0123
              </a>
            </p>
            <p className="mt-2 text-xs text-gray-400">
              Press{' '}
              <kbd className="rounded-md border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800">
                ESC
              </kbd>{' '}
              to go home
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
